import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import styled from '@emotion/styled'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { connectionStoreSelector } from 'selectors/storeSelectors'
import { ConnectionActions } from 'store/connection'

export interface AuthUserSelectionProps {
  lastErrorMessage: string | null
  lastSocketErrorMessage: string | null
  onSetCredentials: (username: string) => unknown
}

const mapStateToProps = createSelector(connectionStoreSelector, connectionStore => {
  return {
    lastErrorMessage: connectionStore.lastErrorMessage,
    lastSocketErrorMessage: connectionStore.lastSocketErrorMessage
  }
})

const mapDispatchToProps = {
  onSetCredentials: ConnectionActions.setCredentials
}

const AuthUserSelectionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  background: #404040;
  overflow-x: scroll;
  height: 100%;

  &:focus {
    outline: none;
    border: none;
  }
`

const ErrorMessage = styled.div`
  white-space: pre-line;
  color: red;
`

const LeftBox = styled.div`
  width: 250px;
`

const RowGroupContainer = styled.div`
  color: ${({ color }) => color ?? '#fff'};
  display: flex;
  flex-direction: row;
  gap: 5px;
  color: #fff;
  padding: 5px;
  padding-bottom: 50px;
  align-content: flex-start;
`
const RowGroupLabel = styled.h2`
  color: #bfbfbf;
`

const RowGroup: React.FC<{
  children?: React.ReactNode
  label?: string
}> = ({ label, children }) => {
  return (
    <RowGroupContainer>
      <LeftBox>
        <RowGroupLabel>{label}</RowGroupLabel>
      </LeftBox>
      {children}
    </RowGroupContainer>
  )
}

const UserCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px;
  width: 300px;
`

const UserGrouping = styled.div`
  display: flex;
  flex-direction: column;
`

const EnvironmentContainer = styled.div`
  color: #bfbfbf;
  display: flex;
  flex-direction: column;
`

const EnvironmentHeader = styled.h2`
  color: #fff;
  padding-left: 275px;
`

const Environment: React.FC<{
  children?: React.ReactNode
  heading: string
}> = ({ heading, children }) => {
  return (
    <EnvironmentContainer>
      <EnvironmentHeader>{heading}</EnvironmentHeader>
      {children}
    </EnvironmentContainer>
  )
}

const UserButton = styled(TextButton)<{ side: Side }>`
  opacity: 0.9;
  align-self: flex-start;
  width: 220px;

  &,
  &:hover {
    border-color: ${({ side }) => (side === Side.BUY ? '#3195ec' : side === Side.NONE ? '#00afaf' : '#e5483e')};
    background-color: ${({ side }) => (side === Side.BUY ? '#3195ec' : side === Side.NONE ? '#00afaf' : '#e5483e')};
  }

  &:hover {
    opacity: 1;
  }
`

const UserCardDetail = styled.span`
  padding-top: 5px;
`

const UserCard: React.FC<{
  label?: string
  username: string
  description?: string
  description2?: string
  description3?: string
  side: Side
}> = ({ username, label, description, description2, description3, side }) => {
  const dispatch = useDispatch()
  const handleSetCredentials = () => dispatch(ConnectionActions.setCredentials(username))

  return (
    <UserCardContainer>
      <UserButton type={TextButtonType.Primary} onClick={handleSetCredentials} side={side} title={username}>
        {label ?? username}
      </UserButton>
      <UserCardDetail>{username}</UserCardDetail>
      <UserCardDetail>{description}</UserCardDetail>
      <UserCardDetail>{description2}</UserCardDetail>
      <UserCardDetail>{description3}</UserCardDetail>
    </UserCardContainer>
  )
}

enum Side {
  NONE,
  BUY,
  SELL
}

const AuthUserSelectionComponent: React.FunctionComponent<AuthUserSelectionProps> = props => {
  const { lastErrorMessage, lastSocketErrorMessage } = props
  const errorMessage = lastSocketErrorMessage || lastErrorMessage

  return (
    <AuthUserSelectionContainer>
      <Environment heading="UAT DATA - Users can be used when the application is started with local uat data">
        <RowGroup label="Traders">
          <UserGrouping>
            <UserCard side={Side.BUY} username="atf1.buyside1@weareadaptive.com" label="Adaptive Test Fund 1" />
            <UserCard side={Side.BUY} username="atf1.buyside2@weareadaptive.com" label="Adaptive Test Fund 1" />
            <UserCard side={Side.BUY} username="atf1.buysideSBL@weareadaptive.com" label="Adaptive Test Fund 1" />
          </UserGrouping>
          <UserGrouping>
            <UserCard side={Side.SELL} username="atb1.sellside1@weareadaptive.com" label="Adaptive Test Bank 1" />
            <UserCard side={Side.SELL} username="atb1.sellside2@weareadaptive.com" label="Adaptive Test Bank 1" />
            <UserCard side={Side.SELL} username="atb1.sellsideSBL@weareadaptive.com" label="Adaptive Test Bank 1" />
          </UserGrouping>
          <UserGrouping>
            <UserCard side={Side.SELL} username="atb2.sellside1@weareadaptive.com" label="Adaptive Test Bank 2" />
          </UserGrouping>
        </RowGroup>

        <RowGroup label="Admin">
          <UserCard
            side={Side.NONE}
            username="at.admin_mc@weareadaptive.com"
            label="Market Control"
            description="This user has all admin permissions including GROUP_ADAPTIVE_ADMIN"
          />
          <UserCard side={Side.NONE} username="at.admin_nomc@weareadaptive.com" label="No Market Control" description="Read only admin" />
          <UserCard side={Side.NONE} username="at.admin_adaptive_support@weareadaptive.com" label="Adaptive support" />
          <UserCard side={Side.NONE} username="at.admin_adaptive_client_tester@weareadaptive.com" label="Adaptive client tester" />
        </RowGroup>
      </Environment>

      <div>{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}</div>
    </AuthUserSelectionContainer>
  )
}

export const AuthUserSelection = connect(mapStateToProps, mapDispatchToProps)(AuthUserSelectionComponent)
